import { useState, useNuxtApp, useRouter } from 'nuxt/app';
import { computed, onMounted } from 'vue';
import { ApiResponse } from "~/types/response";
import { useHandleCode } from './useHandleCode';
import { useFetchWithJwt } from './useFetchWithJwt';
import { usePersistentState} from "~/composables/usePersistentState";
import { MENUS } from "~/constants/menus";
import { PUMP_TYPE } from "~/constants/pumpType";
import { useFacility } from './useFacility';
import { useTerminal } from './useTerminal';

interface MenuItem {
	menuNo: number;
	parentMenuNo: number;
	menuType: string;
	menuName: string;
	menuUrl: string;
}

const SESSION_STORAGE_KEY = 'menuItems';

export function useMenu() {
	const { $log } = useNuxtApp() as unknown as { $log: Console };
	const router = useRouter();
	const { fetchWithJwt } = useFetchWithJwt();
	const { handleErrorCode } = useHandleCode();
	const { facility } = useFacility();
	const { terminals } = useTerminal();

	const menuItems = useState<MenuItem[]>('menuItems', () => []);
	const menuItemCurrentTopParentId = usePersistentState<number>('menuItemCurrentTopParentId',() => 10);//현재 선택된 메뉴의 parent id
	const menuItemCurrentLeftParentId = usePersistentState<number>('menuItemCurrentLeftParentId',() => 0);//현재 선택된 메뉴의 parent id

	// 페이지 로드시 sessionStorage에서 메뉴 로드
	onMounted(() => {
		const storedMenu = sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (storedMenu) {
			menuItems.value = JSON.parse(storedMenu);
		}
	});


	// 현재 시설의 펌프 타입들을 가져오는 computed
	const availablePumpTypes = computed(() => {
		if (!terminals.value || terminals.value.length === 0) return [];
		return [...new Set(terminals.value.map(terminal => terminal.pumpType))];
	});

	// 펌프 타입에 따른 메뉴 필터링 함수
	const getFilteredMenusByPumpType = (menus: MenuItem[]) => {
		if (!availablePumpTypes.value || availablePumpTypes.value.length === 0) {
			return menus;
		}

		// 펌프 타입별 메뉴 번호 매핑
		const pumpTypeMenuMapping = {
			[PUMP_TYPE.FIRE]: [30], // 소방 메뉴 번호
			[PUMP_TYPE.WATER]: [40], // 급수 메뉴 번호  
			[PUMP_TYPE.DRAINAGE]: [50] // 오배수 메뉴 번호
		};

		const allowedMenuNos = new Set<number>();
		availablePumpTypes.value.forEach(pumpType => {
			const menuNos = pumpTypeMenuMapping[pumpType as keyof typeof pumpTypeMenuMapping] || [];
			menuNos.forEach(menuNo => allowedMenuNos.add(menuNo));
		});

		return menus.filter(menu => {
			// MAIN 타입 메뉴 중 펌프 관련 메뉴가 아닌 경우는 모두 포함
			if (menu.menuType === "MAIN" && ![30, 40, 50].includes(menu.menuNo)) {
				return true;
			}
			// 펌프 관련 메뉴인 경우 현재 시설의 펌프 타입에 해당하는 메뉴만 포함
			return allowedMenuNos.has(menu.menuNo);
		});
	};

	const menuItemFirst = computed(() => {
		const filteredMenus = getFilteredMenusByPumpType(menuItems.value);
		return filteredMenus[0] || undefined;
	});
	
	const menuItemSecond = computed(() => {
		const filteredMenus = getFilteredMenusByPumpType(menuItems.value);
		return filteredMenus[1] || undefined;
	});
	
	const menuItemsMain = computed(() => {
		const mainMenus = menuItems.value.filter(menu => menu.menuType === "MAIN");
		return getFilteredMenusByPumpType(mainMenus);
	});
	
	const menuItemsSubOperationsManagement = computed(() => menuItems.value.filter(menu => menu.menuType === "MENU_1" && menu.parentMenuNo == MENUS.OPERATIONS_MANAGEMENT));
	const menuItemsSubSettings = computed(() => menuItems.value.filter(menu => menu.menuType === "MENU_1" && menu.parentMenuNo == MENUS.SETTINGS));

	// 메뉴 리스트 불러오기
	const fetchMainMenu = async (): Promise<void> => {
		try {
			const response = await fetchWithJwt('/menu/user', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
			}) as ApiResponse<MenuItem[]>;

			if (response.result === 'success') {
				menuItems.value = response.data;
				sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(response.data));

				// 화면 크기 체크하여 라우팅 결정
				const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1280;
					
				if (isMobile && menuItemSecond.value?.menuUrl) {
					// 모바일(1280px 이하)일 때 두 번째 메뉴로 라우팅
					// 두 번째 메뉴의 menuNo로 설정
					menuItemCurrentTopParentId.value = menuItemSecond.value.menuNo;
					router.push(menuItemSecond.value.menuUrl);
				} else if (menuItemFirst.value?.menuUrl) {
					// 데스크톱(1280px 초과)일 때 첫 번째 메뉴로 라우팅
					// 첫 번째 메뉴의 menuNo로 설정
					menuItemCurrentTopParentId.value = menuItemFirst.value.menuNo;
					router.push(menuItemFirst.value.menuUrl);
				} else {
					$log.warn("Menu item is empty.");
				}
			} else {
				handleErrorCode(response.message || 'E50001');
			}
		} catch (error) {
			console.error('fetchMainMenu error:', error);
			handleErrorCode('E99999');
		}
	};

	const handleMenuItemCurrentTop = async(menuItem: any) => {
		console.log('[useMenu.ts] handleMenuItemCurrentTop 함수 시작');
		menuItemCurrentTopParentId.value = menuItem.menuNo;
		menuItemCurrentLeftParentId.value = Number(menuItem.menuNo) * 10 + 10;
		console.log('[useMenu.ts] handleMenuItemCurrentTop 함수 종료');
	};
	const handleMenuItemCurrentLeft = async(menuItem: any) => {
		console.log('[useMenu.ts] handleMenuItemCurrentLeft 함수 시작');
		menuItemCurrentLeftParentId.value = menuItem.menuNo;
		console.log('[useMenu.ts] handleMenuItemCurrentLeft 함수 종료');
	};

	return {
		menuItems,
		menuItemCurrentTopParentId,//현재 선택된 상단 메뉴의 id값
		menuItemCurrentLeftParentId,//현재 선택된 좌측 메뉴의 id값
		menuItemFirst,//사용자에게 보여질 처음 페이지
		menuItemSecond,//사용자에게 보여질 두 번째 페이지
		menuItemsMain,//메인 메뉴(상단) - 펌프 타입에 따라 동적 필터링
		menuItemsSubOperationsManagement,//운영관리 좌측(서브) 메뉴
		menuItemsSubSettings,//설정 좌측(서브) 메뉴
		availablePumpTypes,//현재 시설의 펌프 타입들
		fetchMainMenu,
		handleMenuItemCurrentTop,
		handleMenuItemCurrentLeft
	};
}